import { generatePath } from 'react-router-dom';
import { Button } from '@accure/ui';
import { Pencil } from 'lucide-react';
import { tv } from 'tailwind-variants';

import type { Site } from '@/api/fe-api/domain/entities/site';
import { SiteOperationalPhase } from '@/api/fe-api/domain/entities/site-operational-phase';
import {
  OperationalPhase,
  TooltipContent,
} from '@/components/sites/operational-phase';
import { Link } from '@/components/ui/link';
import { Tooltip } from '@/components/ui/tooltip';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { siteLabel } from '@/utils/format/customer';

export type SiteHeroProps = {
  site: Site;
  className?: string;
};

export function SiteHero({ site, className }: SiteHeroProps) {
  const style = siteHeroStyle({ hasImage: Boolean(site.pictureUrl) });
  const featureFlag = useFeatureFlag();
  const isEditable =
    featureFlag('recordsCreate') &&
    site.operationalPhase !== SiteOperationalPhase.MONITORING_ENDED;

  return (
    <div className={style.container({ className })}>
      {site.pictureUrl ? (
        <img src={site.pictureUrl} alt="" className={style.img()} />
      ) : null}
      <div className={style.bg()} />
      <div className={style.content()}>
        <div>
          <h1 className={style.name()}>{siteLabel(site.fullFrontendId)}</h1>
          <p className={style.address()}>{site.address}</p>
        </div>
        <div className={style.contentRight()}>
          <Tooltip content={<TooltipContent site={site} />}>
            <OperationalPhase site={site} />
          </Tooltip>
        </div>
      </div>
      <div className={style.dropdown()}>
        <div className="flex gap-2">
          {isEditable ? (
            <Link
              to={generatePath('/asset/:siteId/overview/edit', {
                siteId: site.id,
              })}
            >
              <Button color="white" className={style.dropdownButton()}>
                <Pencil />
                Edit asset details
              </Button>
            </Link>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export const siteHeroStyle = tv({
  slots: {
    container: `
      group
      relative
      h-36
      w-full
      overflow-hidden
      rounded-t-md
      bg-gray-200
      shadow-inner
      lg:h-72
    `,
    img: `
      size-full
      object-cover
    `,
    bg: `
      absolute
      inset-0
      bg-gradient-to-t
      from-black/70
      to-transparent
    `,
    content: `
      absolute
      inset-x-0
      bottom-0
      grid
      grid-cols-2
      space-y-2
      p-6
      text-white
    `,
    contentRight: `
      flex
      items-end
      justify-self-end
    `,
    name: `
      text-3xl
      font-bold
      text-white
    `,
    address: `
      text-lg
      text-gray-100
    `,
    dropdown: `
      absolute
      right-4
      top-4
    `,
    dropdownButton: `
      bg-white/70
      p-1
      text-zinc-600
      opacity-50
      transition-opacity
      hover:bg-white
      hover:text-zinc-600
      group-hover:opacity-100
    `,
    dropdownMenu: `
      absolute
      right-0
      z-50
    `,
  },
  variants: {
    hasImage: {
      true: {
        dropdownButton: `
          opacity-0
          group-hover:opacity-50
        `,
      },
    },
  },
});
